import { useState } from "react";
import { useTheme, useAuth } from "../App";

function MethodPill({ method }) {
  const c = { GET:"#3fb950", POST:"#58a6ff", PUT:"#d29922", DELETE:"#f85149", PATCH:"#bc8cff" }[method]||"#8b949e";
  return (
    <span style={{ fontFamily:"monospace", fontSize:10, fontWeight:700, padding:"2px 7px",
      borderRadius:4, border:`1px solid ${c}55`, background:`${c}1a`, color:c, flexShrink:0 }}>
      {method}
    </span>
  );
}

export default function Sidebar({ workflows, logs, selected, onSelect, onDeleteLog, onClearLogs, onRefresh }) {
  const { user } = useAuth();
  const { C, accent } = useTheme();
  const [section, setSection] = useState("workflows");
  const [search, setSearch]   = useState("");

  const filtered = workflows.filter(w=>
    w.name.toLowerCase().includes(search.toLowerCase()) ||
    (w.url||"").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ width:230, flexShrink:0, background:C.surface,
      borderRight:`1px solid ${C.border}`, display:"flex", flexDirection:"column", height:"100%" }}>

      <div style={{ display:"flex", borderBottom:`1px solid ${C.border}` }}>
        {["workflows","logs"].map(s=>(
          <button key={s} type="button" onClick={()=>setSection(s)} style={{
            flex:1, padding:"10px 4px", fontSize:11, fontWeight:700, border:"none",
            borderBottom:section===s?`2px solid ${accent}`:"2px solid transparent",
            background:"transparent", color:section===s?C.text:C.muted,
            cursor:"pointer", letterSpacing:0.5, textTransform:"uppercase" }}>{s}</button>
        ))}
      </div>

      {section==="workflows" && <>
        <div style={{ padding:"8px 10px", borderBottom:`1px solid ${C.border}` }}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search workflows..."
            className="input-field" style={{ padding:"6px 10px", fontSize:12 }}/>
        </div>
        <div style={{ flex:1, overflowY:"auto" }}>
          <div onClick={()=>onSelect(null)} style={{
            padding:"10px 14px", cursor:"pointer", borderBottom:`1px solid ${C.border}20`,
            background:selected===null?C.panel:"transparent",
            borderLeft:selected===null?`3px solid ${accent}`:"3px solid transparent" }}>
            <span style={{ fontSize:12, color:"#388bfd", fontWeight:700 }}>+ New request</span>
          </div>
          {!user && <div style={{ padding:"20px 14px", fontSize:12, color:C.faint, lineHeight:1.5 }}>Sign in to save requests and view them here.</div>}
          {user && filtered.length===0 && <div style={{ padding:"20px 14px", fontSize:12, color:C.faint }}>No saved workflows.</div>}
          {user && filtered.map(wf=>(
            <div key={wf.id} onClick={()=>onSelect(wf)} style={{
              padding:"10px 14px", cursor:"pointer", borderBottom:`1px solid ${C.border}15`,
              background:selected?.id===wf.id?C.panel:"transparent",
              borderLeft:selected?.id===wf.id?`3px solid ${accent}`:"3px solid transparent",
              transition:"background 0.1s" }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                <span style={{ width:7, height:7, borderRadius:"50%", flexShrink:0,
                  background:wf.active?"#3fb950":C.border,
                  boxShadow:wf.active?"0 0 6px #3fb95066":"none" }}/>
                <span style={{ fontSize:13, fontWeight:600, color:C.text,
                  overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{wf.name}</span>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:6, paddingLeft:15 }}>
                <MethodPill method={wf.method||"GET"}/>
                <span style={{ fontSize:10, color:C.muted, overflow:"hidden",
                  textOverflow:"ellipsis", whiteSpace:"nowrap", fontFamily:"monospace" }}>{wf.url}</span>
              </div>
            </div>
          ))}
        </div>
      </>}

      {section==="logs" && <>
        <div style={{ padding:"8px 10px", borderBottom:`1px solid ${C.border}` }}>
          <button type="button" onClick={onClearLogs} style={{ width:"100%", background:"transparent",
            border:"1px solid #f8514930", borderRadius:6, padding:"5px", color:"#f85149",
            cursor:"pointer", fontSize:11, fontWeight:600 }}>🗑 Clear all logs</button>
        </div>
        <div style={{ flex:1, overflowY:"auto" }}>
          {!user && <div style={{ padding:"20px 14px", fontSize:12, color:C.faint, lineHeight:1.5 }}>Background logs persist securely when you have an account.</div>}
          {user && logs.length===0 && <div style={{ padding:"20px 14px", fontSize:12, color:C.faint }}>No logs yet.</div>}
          {user && logs.map((log,i)=>{
            const c = log.status==="success"?"#3fb950":log.status==="error"?"#f85149":"#58a6ff";
            return (
              <div key={log.id||i} style={{ padding:"9px 14px", borderBottom:`1px solid ${C.border}15`,
                display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ width:6, height:6, borderRadius:"50%", background:c, flexShrink:0 }}/>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:C.text,
                    whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{log.name}</div>
                  <div style={{ fontSize:10, color:C.muted, fontFamily:"monospace", marginTop:2 }}>
                    {log.status}{log.duration!=null?` · ${log.duration}ms`:""} · {new Date(log.time).toLocaleTimeString()}
                  </div>
                </div>
                <button type="button" onClick={()=>onDeleteLog(log.id)} style={{ background:"none",
                  border:"none", color:"#f85149", cursor:"pointer", fontSize:14, opacity:0.45, padding:"0 3px" }}
                  onMouseEnter={e=>e.currentTarget.style.opacity="1"}
                  onMouseLeave={e=>e.currentTarget.style.opacity="0.45"}>×</button>
              </div>
            );
          })}
        </div>
      </>}

      <div style={{ padding:"8px 10px", borderTop:`1px solid ${C.border}` }}>
        <button type="button" onClick={onRefresh} style={{ width:"100%", background:C.bg,
          border:`1px solid ${C.border}`, borderRadius:6, padding:"7px", color:C.muted,
          cursor:"pointer", fontSize:11, fontWeight:600 }}>
          ↻ Refresh
        </button>
      </div>
    </div>
  );
}
